import React, { useEffect, useState } from "react";
import Briciole from "@/components/Briciole";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Wrench, Truck } from "lucide-react";
import AnalisiCostiMezzi from "@/components/AnalisiCostiMezzi";
import BarraFiltri from "@/components/BarraFiltri";

export default function CostiManutenzione() {
  const [data, setData] = useState(null);
  const [perCategoria, setPerCategoria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearDetail, setYearDetail] = useState(null);

  useEffect(() => {
    api.get("/equipment/maintenance/costs-by-year")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Errore caricamento costi manutenzione"))
      .finally(() => setLoading(false));
    api.get("/equipment/maintenance/costs-by-category")
      .then(({ data }) => setPerCategoria(data))
      .catch(() => {});
  }, []);

  const totalAll = (data || []).reduce((s, y) => s + y.total_cost, 0);
  const totalCount = (data || []).reduce((s, y) => s + y.count, 0);

  return (
    <div className="space-y-6" data-testid="costi-manutenzione-page">
      <Briciole voci={[
        { testo: "Mezzi e attrezzature", a: "/attrezzature" },
        { testo: "Costi manutenzione" },
      ]} />

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Mezzi e attrezzature</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Costi manutenzione</h1>
        <p className="text-sm text-slate-500 mt-1">Riepilogo di tutti i mezzi, anno per anno</p>
      </div>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!loading && (!data || data.length === 0) && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessun intervento di manutenzione registrato ancora.
        </CardContent></Card>
      )}

      {!loading && data && data.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Costo totale storico</div>
                <div className="font-display text-2xl font-black">€ {totalAll.toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Interventi totali</div>
                <div className="font-display text-2xl font-black">{totalCount}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Anni coperti</div>
                <div className="font-display text-2xl font-black">{data.length}</div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display text-lg font-bold mb-3">Costo totale per anno · tutti i mezzi</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => `€ ${Number(v).toFixed(2)}`} />
                  <Bar dataKey="total_cost" fill="#0067b2" radius={[4, 4, 0, 0]} className="cursor-pointer"
                       onClick={(payload) => { if (payload?.year) setYearDetail(payload.year); }} />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-slate-400 text-center mt-2">Clicca una barra per vedere tutti gli interventi di quell'anno, mezzo per mezzo</p>
            </CardContent>
          </Card>

          {perCategoria.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-1">Costo totale per categoria</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Dalla più costosa alla meno costosa. La percentuale è sul totale storico.
                </p>
                <div className="space-y-3">
                  {perCategoria.map(c => {
                    const quota = totalAll > 0 ? (c.total_cost / totalAll) * 100 : 0;
                    return (
                      <div key={c.category}>
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="font-semibold truncate">{c.category}</span>
                          <span className="font-display font-black whitespace-nowrap">
                            € {c.total_cost.toFixed(2)}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded mt-1 overflow-hidden">
                          <div className="h-full bg-[#0067b2] rounded"
                               style={{ width: `${Math.max(quota, c.total_cost > 0 ? 1 : 0)}%` }} />
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {quota.toFixed(1)}% · {c.count} intervent{c.count === 1 ? "o" : "i"} su{" "}
                          {c.equipment_with_costs} di {c.equipment_count} macchin{c.equipment_count === 1 ? "a" : "e"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          <AnalisiCostiMezzi />
        </>
      )}

      {yearDetail && <YearDetailDialog year={yearDetail} onClose={() => setYearDetail(null)} />}
    </div>
  );
}

function YearDetailDialog({ year, onClose }) {
  const [logs, setLogs] = useState(null);
  // Un anno di interventi su tutto il parco sono centinaia di righe:
  // senza una ricerca, trovare quelli di una macchina vuol dire scorrere.
  const [cerca, setCerca] = useState("");

  useEffect(() => {
    api.get(`/equipment/maintenance/year/${year}`).then(({ data }) => setLogs(data)).catch(() => toast.error("Errore caricamento"));
  }, [year]);

  const q = cerca.trim().toLowerCase();
  const visibili = q
    ? (logs || []).filter((l) =>
        `${l.equipment_identifier || ""} ${l.description || ""} ${l.supplier || ""}`.toLowerCase().includes(q))
    : (logs || []);
  const total = visibili.reduce((s, l) => s + (l.cost || 0), 0);
  // Il riepilogo per mezzo guarda quello che si sta vedendo: se la ricerca
  // stringe su una macchina, anche i totali sopra devono parlare di quella.
  const byEquipment = {};
  visibili.forEach(l => {
    const key = l.equipment_identifier || "—";
    byEquipment[key] = byEquipment[key] || { total: 0, count: 0, category: l.equipment_category };
    byEquipment[key].total += l.cost || 0;
    byEquipment[key].count += 1;
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Interventi {year} · tutti i mezzi</DialogTitle></DialogHeader>
        {!logs ? <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p> : (
          <div className="space-y-4">
            <BarraFiltri cerca={cerca} onCerca={setCerca}
                         segnaposto="Targa, descrizione o fornitore..." />
            <div className="text-sm text-slate-600">
              {visibili.length} interventi su {Object.keys(byEquipment).length} mezzi · totale <strong>€ {total.toFixed(2)}</strong>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-2">Per mezzo</h4>
              <div className="space-y-1.5">
                {Object.entries(byEquipment).sort((a, b) => b[1].total - a[1].total).map(([id, v]) => (
                  <div key={id} className="flex items-center justify-between text-sm p-2 border border-slate-200 rounded">
                    <span className="flex items-center gap-1.5 font-mono">
                      {v.category?.includes("AUTO") ? <Truck className="w-3.5 h-3.5 text-slate-400" /> : <Wrench className="w-3.5 h-3.5 text-slate-400" />}
                      {id}
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{v.count} interventi</Badge>
                      <strong>€ {v.total.toFixed(2)}</strong>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-2">Dettaglio interventi</h4>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {visibili.map(l => (
                  <div key={l.id} className="p-2 border border-slate-100 rounded text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{l.date?.substring(0, 10)} · {l.equipment_identifier}</span>
                      <span>{l.cost ? `€ ${l.cost.toFixed(2)}` : "—"}</span>
                    </div>
                    <div className="text-slate-500">{l.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
